/**
 * usePlanOrchestration — all plan pipeline logic for both Quick and Full modes.
 * AIPropertiesChat delegates to this hook; it never contains inline pipeline logic.
 *
 * This file is now a thin coordinator. Logic is split into three focused hooks:
 *   - usePlanWizardHandlers  (Groups A + D)
 *   - usePlanQualityGate     (Group C)
 *   - usePlanPhaseExecution  (Group B)
 */
import { useRef, useEffect, useCallback } from 'react';
import { usePlanStore } from '@/stores/ai/plan-store';
import { useDebugStore } from '@/stores/ai/debug-store';
import { planApi } from '@/services/api/agentApi';

import { EditorContextSerializer } from '@/services/editor/EditorContextSerializer';
import type { PropertyMessage } from '@/components/ai/types';
import type { AuditResult, AuditCriterion, PlanPipelineMode, PlanPipelineStage } from '@/types/plan';
import { PIPELINE_STAGE_LABELS } from '@/types/plan';
import type { Schema } from '@/services/api/schemaApi';
import { usePlanWizardHandlers } from './usePlanWizardHandlers';
import { usePlanQualityGate } from './usePlanQualityGate';
import { usePlanPhaseExecution } from './usePlanPhaseExecution';
import { isAbortError, toErrMsg, captureDebugPrompts } from './planHelpers';
import type { PhaseExecutionRefs } from './usePlanPhaseExecution';

/**
 * Module-level cache for schema context fetched from the data service.
 * Populated on first wizard message that needs schema context; cleared when
 * the plan store deactivates. This avoids re-fetching on every wizard turn
 * without polluting the Zustand store with non-reactive internal state.
 */
let _schemaContextCache: Array<{ name: string; groupName: string; fields: Array<{ name: string; type: string }> }> | null = null;

// ─────────────────────────────────────────────────────────────────────────────
// Dependencies interface
// ─────────────────────────────────────────────────────────────────────────────

export interface PlanOrchestrationDeps {
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
  persistMessage: (msg: PropertyMessage) => void;
  setInputValue: (val: string) => void;
  setIsLoading: (val: boolean) => void;
  generateMessageId: () => string;
  communityId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePlanOrchestration(deps: PlanOrchestrationDeps) {
  const planStore = usePlanStore();
  const abortControllerRef = useRef<AbortController | null>(null);
  const accumulatedArtifacts = useRef<Record<string, Record<string, string[]>>>({});
  const accumulatedFeedback = useRef<string[]>([]);
  const executionStartTimeRef = useRef<number>(0);
  const isPausedRef = useRef(false);

  // C-2: Cleanup on unmount — abort any in-flight operations
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Clear module-level schema cache when plan deactivates
  useEffect(() => {
    const unsub = usePlanStore.subscribe(
      (s) => s.isActive,
      (isActive) => { if (!isActive) _schemaContextCache = null; },
    );
    return unsub;
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function createAbortController(): AbortController {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    planStore.setAbortController(controller);
    return controller;
  }

  const debug = useDebugStore;
  function debugEvent(type: string, data: unknown) {
    debug.getState().pushEvent({ type, data, timestamp: Date.now() });
  }

  function planStage(stage: PlanPipelineStage) {
    const ds = debug.getState();
    if (ds.isEnabled && ds.planDebugData) {
      ds.recordPlanStageTransition(stage, PIPELINE_STAGE_LABELS[stage] || stage);
    }
  }

  /** Wraps stage transition with timing recording */
  function advanceStage(newStage: PlanPipelineStage) {
    const currentStage = usePlanStore.getState().pipelineStage;
    planStore.recordStageEnd(currentStage);
    planStore.setPipelineStage(newStage);
    planStore.recordStageStart(newStage);
    planStage(newStage);
  }

  function planQualityGate(gate: { stage: string; phaseId?: string; verdict: string; score?: number; action: string; retryNumber?: number }) {
    const ds = debug.getState();
    if (ds.isEnabled && ds.planDebugData) {
      ds.recordPlanQualityGate(gate);
    }
  }

  function addUIMessage(
    content: string,
    role: 'user' | 'assistant',
    metadata?: Record<string, unknown>,
  ) {
    const msg: PropertyMessage = {
      id: deps.generateMessageId(),
      content,
      role,
      timestamp: new Date(),
      metadata,
    };
    deps.setMessages(prev => [...prev, msg]);
    deps.persistMessage(msg);
    return msg;
  }

  // ── Schema Context Builder (shared across page-design, workflow, compile) ──

  /** Schema context type used by wizard handlers and compile */
  type SchemaContextEntry = { name: string; groupName: string; fields: Array<{ name: string; type: string; required?: boolean; description?: string }> };

  /**
   * Builds schema context from two sources:
   * 1. proposedSchema from the current guided flow session (may not be published yet)
   * 2. Published schemas from the data service API (fetched once, cached in module-level _schemaContextCache)
   *
   * Returns undefined if no schemas are available. Swallows errors (non-fatal).
   */
  async function buildSchemaContext(
    ps: ReturnType<typeof usePlanStore.getState>,
  ): Promise<SchemaContextEntry[] | undefined> {
    try {
      const schemas: SchemaContextEntry[] = [];

      // Source 1: proposedSchema from current wizard session (may not be published yet)
      const proposed = ps.proposedSchema;
      if (proposed) {
        schemas.push({
          name: proposed.name,
          groupName: proposed.groupName || proposed.name,
          fields: (proposed.fields || []).map((f: { name: string; type: string; required?: boolean; description?: string }) => ({ name: f.name, type: f.type, required: f.required, description: f.description })),
        });
      }

      // Source 2: Published schemas from data service (fetch once, cache for session)
      if (!_schemaContextCache) {
        const { schemaApi } = await import('@/services/api/schemaApi');
        const result = await schemaApi.listSchemas({ limit: 50, status: 'published' });
        _schemaContextCache = result.data?.length
          ? result.data.map((s: Schema) => ({
              name: s.name,
              groupName: s.displayName || s.name,
              fields: (s.fields || []).map((f: { name: string; type: string; required?: boolean; description?: string }) => ({ name: f.name, type: f.type, required: f.required, description: f.description })),
            }))
          : [];
      }

      // Merge, deduplicating by name (proposed takes precedence)
      const seenNames = new Set(schemas.map(s => s.name));
      for (const s of _schemaContextCache) {
        if (!seenNames.has(s.name)) {
          schemas.push(s);
          seenNames.add(s.name);
        }
      }

      return schemas.length > 0 ? schemas : undefined;
    } catch {
      return undefined; // non-fatal — proceed without schema context
    }
  }

  // ── Quick Mode: compile ────────────────────────────────────────────────────

  async function runQuickModeCompile(signal: AbortSignal) {
    advanceStage('reviewing');
    planStore.setCompiling(true);
    const isDebug = debug.getState().isEnabled;
    const compileStart = Date.now();
    debugEvent('step_start', { step: 'plan_compile', agent: 'PlanService' });
    try {
      // Build schema context (uses module-level cache from wizard handlers)
      const schemaContext = await buildSchemaContext(planStore);

      // Build knowledge context from plan store
      const kb = planStore.proposedKnowledgeBase;
      const knowledgeContext = kb
        ? { title: kb.title, description: kb.description, domain: kb.domain,
            articles: kb.articles?.map(a => ({ title: a.title, outline: a.outline })),
            tags: kb.tags }
        : undefined;

      const compiled = await planApi.compile(
        planStore.conversationHistory,
        EditorContextSerializer.serialize(),
        planStore.currentThreadId ?? undefined,
        signal,
        isDebug,
        schemaContext,
        knowledgeContext,
      );
      planStore.incrementLLMCalls();
      captureDebugPrompts(compiled, 'compile');
      planStore.setCompiling(false);
      debugEvent('step_complete', { step: 'plan_compile', duration_ms: Date.now() - compileStart });
      if (compiled.data?.plan) {
        planStore.setPlan(compiled.data.plan);
        if (compiled.data.threadId) planStore.setThreadId(compiled.data.threadId);
      }
      debug.getState().endSession();
    } catch (err) {
      planStore.setCompiling(false);
      debugEvent('error', { stage: 'plan_compile', error: toErrMsg(err) });
      debug.getState().endSession();
      throw err;
    }
  }

  // ── Full Mode: research → roadmap → audit → review ─────────────────────────

  async function runFullModePipeline(signal: AbortSignal, researchSuggestions?: string[]) {
    const history = planStore.conversationHistory;
    const threadId = planStore.currentThreadId ?? undefined;
    const isDebug = debug.getState().isEnabled;

    // a. Research (if suggestions available)
    if (researchSuggestions?.length) {
      advanceStage('researching');
      planStore.setResearching(true);
      const researchStart = Date.now();
      debugEvent('step_start', { step: 'plan_research', agent: 'WebResearchService' });
      try {
        const researchResult = await planApi.research(
          researchSuggestions,
          history,
          threadId,
          signal,
        );
        planStore.incrementLLMCalls();
        planStore.setResearching(false);
        debugEvent('step_complete', { step: 'plan_research', duration_ms: Date.now() - researchStart });
        if (researchResult.data?.researchContext) {
          planStore.setResearchContext(researchResult.data.researchContext);
        }
      } catch (err) {
        planStore.setResearching(false);
        debugEvent('error', { stage: 'plan_research', error: toErrMsg(err) });
        throw err;
      }
    }

    // b. Roadmap generation (or regeneration if refining an existing roadmap)
    advanceStage('generating_roadmap');
    planStore.setGeneratingRoadmap(true);
    const editorCtx = EditorContextSerializer.serialize();
    const existingRoadmap = planStore.roadmap;
    let roadmapStart = Date.now();
    debugEvent('step_start', { step: existingRoadmap ? 'plan_roadmap_regenerate' : 'plan_roadmap_generate', agent: 'RoadmapGenerationService' });

    let roadmapResult;
    if (existingRoadmap) {
      // Refinement mode: regenerate from existing roadmap + recent conversation
      const refinementFeedback = history
        .slice(-6)
        .map(m => `${m.role}: ${m.content}`)
        .join('\n');
      roadmapResult = await planApi.regenerateRoadmap(
        existingRoadmap,
        refinementFeedback,
        history,
        threadId,
        signal,
        isDebug,
        'user',
      );
    } else {
      // Fresh generation
      roadmapResult = await planApi.generateRoadmap(
        history,
        planStore.researchContext,
        editorCtx,
        threadId,
        signal,
        isDebug,
      );
    }
    planStore.incrementLLMCalls();
    captureDebugPrompts(roadmapResult, existingRoadmap ? 'roadmap_regenerate' : 'roadmap_generate');
    planStore.setGeneratingRoadmap(false);
    debugEvent('step_complete', { step: existingRoadmap ? 'plan_roadmap_regenerate' : 'plan_roadmap_generate', duration_ms: Date.now() - roadmapStart });
    if (!roadmapResult.data?.roadmap) {
      planStore.setError('Failed to generate roadmap');
      advanceStage('failed');
      return;
    }
    let roadmap = roadmapResult.data.roadmap;
    planStore.setRoadmap(roadmap);

    // c. Roadmap audit with retry (MED-6: max 2 retries on FAIL)
    advanceStage('auditing_roadmap');
    planStore.setAuditingRoadmap(true);
    const auditStart = Date.now();
    debugEvent('step_start', { step: 'plan_roadmap_audit', agent: 'PlanAuditService' });
    let audit = await planApi.auditRoadmap(roadmap, history, planStore.researchContext ?? undefined, signal, isDebug);
    planStore.incrementLLMCalls();
    captureDebugPrompts(audit, 'roadmap_audit');
    let retries = 0;
    while (audit.data?.auditResult?.verdict !== 'pass' && retries < 2) {
      retries++;
      debugEvent('quality_gate', { stage: 'plan_roadmap_audit', verdict: audit.data?.auditResult?.verdict, retry: retries });
      planQualityGate({ stage: 'auditing_roadmap', verdict: 'fail', score: audit.data?.auditResult?.overallScore, action: 'retry', retryNumber: retries });
      const auditFeedback = JSON.stringify(audit.data.auditResult);
      const regenerated = await planApi.regenerateRoadmap(
        roadmap, auditFeedback, history, threadId, signal, isDebug, 'audit',
      );
      planStore.incrementLLMCalls();
      captureDebugPrompts(regenerated, 'roadmap_regenerate');
      if (regenerated.data?.roadmap) {
        roadmap = regenerated.data.roadmap;
        planStore.setRoadmap(roadmap);
      }
      audit = await planApi.auditRoadmap(roadmap, history, planStore.researchContext ?? undefined, signal, isDebug);
      planStore.incrementLLMCalls();
      captureDebugPrompts(audit, 'roadmap_audit');
    }
    planStore.setAuditingRoadmap(false);
    const auditResult = audit.data?.auditResult;
    if (!auditResult) {
      planStore.setError('Roadmap audit returned no result');
      advanceStage('failed');
      return;
    }
    planStore.setRoadmapAudit(auditResult);
    debugEvent('step_complete', {
      step: 'plan_roadmap_audit',
      duration_ms: Date.now() - auditStart,
      verdict: auditResult.verdict,
      score: auditResult.overallScore,
      retries,
    });

    // d. Quality gate for warn/fail
    if (auditResult.verdict === 'warn') {
      debugEvent('quality_gate', { stage: 'plan_roadmap_audit', verdict: 'warn', score: auditResult.overallScore, action: 'user_review' });
      planQualityGate({ stage: 'auditing_roadmap', verdict: 'warn', score: auditResult.overallScore, action: 'user_review' });
      planStore.setActiveQualityGate({
        reason: `Roadmap audit scored ${auditResult.overallScore}/100 (WARN)`,
        suggestedAction: 'return_to_planning',
        suggestedMessage: 'Review warnings and decide whether to proceed',
        auditResult,
      });
    } else if (auditResult.verdict === 'fail') {
      debugEvent('quality_gate', { stage: 'plan_roadmap_audit', verdict: 'fail', score: auditResult.overallScore, action: 'return_to_planning' });
      planQualityGate({ stage: 'auditing_roadmap', verdict: 'fail', score: auditResult.overallScore, action: 'return_to_planning' });
      planStore.setActiveQualityGate({
        reason: `Roadmap audit failed after ${retries} retries (score: ${auditResult.overallScore}/100)`,
        suggestedAction: 'return_to_planning',
        suggestedMessage: 'Consider revising the plan with more specific requirements',
        auditResult,
      });
    }

    // e. Checkpoint 1
    advanceStage('reviewing');
  }

  // ── Delegate to sub-hooks ──────────────────────────────────────────────────

  const phaseRefs: PhaseExecutionRefs = {
    abortControllerRef,
    accumulatedArtifacts,
    accumulatedFeedback,
    executionStartTimeRef,
    isPausedRef,
  };

  const phaseExecution = usePlanPhaseExecution(
    deps,
    phaseRefs,
    advanceStage,
    addUIMessage,
  );

  const qualityGate = usePlanQualityGate(
    deps,
    advanceStage,
    addUIMessage,
    abortControllerRef,
  );

  const wizardHandlers = usePlanWizardHandlers(
    deps,
    advanceStage,
    addUIMessage,
    buildSchemaContext,
    runQuickModeCompile,
    createAbortController,
  );

  // ── handlePlanMessage ──────────────────────────────────────────────────────

  const handlePlanMessage = useCallback(async (userInput: string) => {
    // Auto-detect creation intents when no specific wizard is active yet
    // (covers both null and 'quick' — quick mode hasn't committed to a wizard)
    // Resolve the effective pipeline mode: detect intent if no wizard is active yet
    let effectiveMode = planStore.pipelineMode;
    if (!effectiveMode || effectiveMode === 'quick') {
      const { intent } = await wizardHandlers.classifyWizardIntent(userInput);
      const detectedMode = wizardHandlers.INTENT_TO_PIPELINE[intent] ?? null;
      if (detectedMode) {
        planStore.setPipelineMode(detectedMode);
        effectiveMode = detectedMode; // Use local var — store closure is stale
      }
    }
    if (effectiveMode === 'skill') { await wizardHandlers.handleSkillWizardMessage(userInput); return; }
    if (effectiveMode === 'agent') { await wizardHandlers.handleAgentWizardMessage(userInput); return; }
    if (effectiveMode === 'knowledge') { await wizardHandlers.handleKnowledgeWizardMessage(userInput); return; }
    if (effectiveMode === 'workflow') { await wizardHandlers.handleWorkflowWizardMessage(userInput); return; }
    if (effectiveMode === 'data') { await wizardHandlers.handleDataWizardMessage(userInput); return; }
    if (effectiveMode === 'page-design') {
      // After execution/discard completed, treat new messages as a fresh page design conversation
      if (planStore.pipelineStage === 'complete' || planStore.pipelineStage === 'idle') {
        planStore.setPageDesignReady(false);
        planStore.setProposedPageDesign(null);
        planStore.setPlanReady(false);
        planStore.setPlan(null);
        planStore.setPipelineStage('conversing');
        // Keep conversationHistory so the wizard has context from the previous exchange
      }
      // Retry path: if design was already captured but compile failed,
      // re-trigger compile instead of sending another wizard message.
      if (planStore.pageDesignReady && planStore.pipelineStage === 'failed') {
        deps.setIsLoading(true);
        try {
          const controller = createAbortController();
          await runQuickModeCompile(controller.signal);
        } catch (err: unknown) {
          if (!isAbortError(err)) {
            addUIMessage('Compile retry failed. Please try again.', 'assistant', { source: 'page-design-wizard', error: true });
          }
        } finally { deps.setIsLoading(false); }
        return;
      }
      await wizardHandlers.handlePageDesignWizardMessage(userInput);
      return;
    }

    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);

    debug.getState().startSession(userInput, 'plan-mode');

    try {
      const controller = createAbortController();
      const signal = controller.signal;

      // UX-3: Auto-context on first message
      const editorCtx = planStore.conversationHistory.length === 0
        ? EditorContextSerializer.serialize()
        : undefined;

      // Inject existing roadmap context when refining (so the LLM knows what to modify)
      const existingRoadmap = planStore.roadmap;
      const converseMessage = existingRoadmap
        ? `${userInput}\n\n[CURRENT ROADMAP FOR REFINEMENT: ${JSON.stringify({
            title: existingRoadmap.title,
            phases: existingRoadmap.phases.map(p => ({
              id: p.id, phaseNumber: p.phaseNumber, title: p.title,
              domain: p.domain, tasks: p.tasks, estimatedComplexity: p.estimatedComplexity,
            })),
          })}]`
        : userInput;

      const isDebug = debug.getState().isEnabled;
      const converseStart = Date.now();
      debugEvent('step_start', { step: 'plan_converse', agent: 'PlanService' });

      const result = await planApi.converse(
        converseMessage,
        planStore.conversationHistory,
        editorCtx,
        planStore.currentThreadId ?? undefined,
        planStore.researchContext,
        signal,
        isDebug,
      );
      planStore.incrementLLMCalls();
      captureDebugPrompts(result, 'converse');

      const response = result.data;
      debugEvent('step_complete', { step: 'plan_converse', duration_ms: Date.now() - converseStart, planReady: response.planReady });

      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);
      if (response.discoveredSchemas?.length) {
        planStore.addDiscoveredSchemas(response.discoveredSchemas);
      }

      addUIMessage(response.message, 'assistant', {
        source: 'plan-mode',
        planReady: response.planReady,
        ...(response.questions?.length ? { questions: response.questions } : {}),
      });

      // Branch by planReady
      if (response.planReady) {
        // Initialize plan debug data for the full pipeline
        if (debug.getState().isEnabled) {
          debug.getState().initPlanDebug();
          planStage('conversing');
          debug.getState().completePlanStage('conversing');
        }

        // Record pipeline start and close the conversing stage timing
        planStore.setPipelineStartedAt(Date.now());
        planStore.recordStageStart('conversing');
        planStore.recordStageEnd('conversing');

        if (planStore.pipelineMode === 'quick') {
          await runQuickModeCompile(signal);
        } else {
          planStore.setPlanReady(true);
          await runFullModePipeline(signal, response.researchSuggestions);
        }
      } else {
        debug.getState().endSession();
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        const message = toErrMsg(err, 'Plan conversation failed');
        debugEvent('error', { stage: 'plan_converse', error: message });
        debug.getState().endSession();
        planStore.setError(message);
        advanceStage('failed');
      }
    } finally {
      deps.setIsLoading(false);
    }
  }, [deps, planStore, wizardHandlers]);

  return {
    handlePlanMessage,
    tryAutoActivate: wizardHandlers.tryAutoActivate,
    advanceToPhaseDecomposition: phaseExecution.advanceToPhaseDecomposition,
    executePhase: phaseExecution.executePhase,
    executeAllPhases: phaseExecution.executeAllPhases,
    executePlan: phaseExecution.executePlan,
    handleQualityGate: qualityGate.handleQualityGate,
    modifyPhase: qualityGate.modifyPhase,
    refineRoadmapWithChat: qualityGate.refineRoadmapWithChat,
    modifyRoadmapPhase: qualityGate.modifyRoadmapPhase,
    pauseExecution: phaseExecution.pauseExecution,
    retryFromStage: phaseExecution.retryFromStage,
    confirmSkillCreation: wizardHandlers.confirmSkillCreation,
    confirmAgentCreation: wizardHandlers.confirmAgentCreation,
    confirmWorkflowCreation: wizardHandlers.confirmWorkflowCreation,
    confirmDataSchemaCreation: wizardHandlers.confirmDataSchemaCreation,
  };
}
